import type { IHomeSystem } from '@/domain/entities/system';
import { normalizeDeep } from '@/lib/dataSanity';
import { getApiEndpoints } from '@/services/api/endpoints';
import type { CreateItemResponse, ListSystemsResponse } from '@/services/api/types';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';

// -------------------------
// Fetch list by property
// -------------------------
export const fetchPropertySystemsByPropertyId = createAppAsyncThunk<IHomeSystem[], { propertyID: string }>(
  'propertySystems/fetchByPropertyId',
  async ({ propertyID }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint =
        getApiEndpoints().core.propertySystems.getPropertySystems + `?propertyID=${encodeURIComponent(propertyID)}`;
      const res = await apiService.get<ListSystemsResponse>(endpoint);
      return Array.isArray(res.items) ? res.items : [];
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);

// -------------------------
// Fetch one
// -------------------------
export const fetchPropertySystemById = createAppAsyncThunk<IHomeSystem, { id: string }>(
  'propertySystems/fetchById',
  async ({ id }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = getApiEndpoints().core.propertySystems.getPropertySystems + `?id=${encodeURIComponent(id)}`;
      return await apiService.get<IHomeSystem>(endpoint);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);

// -------------------------
// Create
// -------------------------
export const createPropertySystem = createAppAsyncThunk<
  CreateItemResponse<IHomeSystem>,
  { system: IHomeSystem; tempId?: string }
>('propertySystems/create', async ({ system }, { extra: { config }, rejectWithValue }) => {
  const { apiService } = config;

  try {
    const endpoint = getApiEndpoints().core.propertySystems.createPropertySystem;
    return await apiService.post<CreateItemResponse<IHomeSystem>>(endpoint, normalizeDeep(system));
  } catch (err) {
    const e = err as Error & { status?: number; body?: string };
    return rejectWithValue({ message: e.message });
  }
});

// -------------------------
// Update
// -------------------------
export const updatePropertySystem = createAppAsyncThunk<
  IHomeSystem,
  { id: string; propertyID: string; patch: Partial<IHomeSystem> }
>('propertySystems/update', async ({ id, propertyID, patch }, { extra: { config }, rejectWithValue }) => {
  const { apiService } = config;

  try {
    const endpoint =
      getApiEndpoints().core.propertySystems.updatePropertySystem +
      `?id=${encodeURIComponent(id)}&propertyID=${encodeURIComponent(propertyID)}`;
    return await apiService.put<IHomeSystem>(endpoint, normalizeDeep(patch));
  } catch (err) {
    const e = err as Error & { status?: number; body?: string };
    return rejectWithValue({ message: e.message });
  }
});

// -------------------------
// Delete
// -------------------------
export const deletePropertySystem = createAppAsyncThunk<{ id: string }, { id: string; propertyID: string }>(
  'propertySystems/delete',
  async ({ id, propertyID }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint =
        getApiEndpoints().core.propertySystems.deletePropertySystem +
        `?id=${encodeURIComponent(id)}&propertyID=${encodeURIComponent(propertyID)}`;
      return await apiService.delete<{ id: string }>(endpoint);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);
